import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Title2,
  Subtitle1,
} from '@fluentui/react-components';
import {
  Settings24Regular,
  ArrowSync24Regular,
  Delete24Regular,
  Warning24Regular,
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  Calculator24Regular,
} from '@fluentui/react-icons';
import { maintenanceService } from '../../services/maintenanceService';
import { ConfirmationModal } from '../../components/Maintenance/ConfirmationModal';
import type { MaintenanceStatusResult, MaintenanceResult, Severity, MaintenanceJobStatus } from '../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    marginBottom: '24px',
    display: 'block',
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  card: {
    padding: '20px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  iconSafe: { color: tokens.colorPaletteGreenForeground1 },
  iconWarning: { color: tokens.colorPaletteYellowForeground1 },
  iconDestructive: { color: tokens.colorPaletteRedForeground1 },
  cardDescription: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    marginBottom: '16px',
    display: 'block',
  },
  statusSection: { marginTop: '32px' },
  statusCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  statusCard: { padding: '16px' },
  statusItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statusExists: { color: tokens.colorPaletteGreenForeground1 },
  statusNotExists: { color: tokens.colorNeutralForeground4 },
  resultMessage: { marginBottom: '24px' },
});

interface ActionConfig {
  id: string;
  title: string;
  description: string;
  severity: Severity;
  icon: React.ReactElement;
  affectedItems: string[];
  action: (text: string) => Promise<MaintenanceResult>;
}

const MaintenancePage: React.FC = () => {
  const styles = useStyles();
  const [status, setStatus] = useState<MaintenanceStatusResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<MaintenanceResult | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionConfig | null>(null);
  const [jobStatus, setJobStatus] = useState<MaintenanceJobStatus | null>(null);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await maintenanceService.getStatus();
      setStatus(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const actions: ActionConfig[] = [
    {
      id: 'reindex',
      title: 'Reindexar Dados',
      description: 'Apaga o indice do Azure Search e recria com base nos dados da tabela EventsOperations.',
      severity: 'safe',
      icon: <ArrowSync24Regular />,
      affectedItems: ['Indice Azure Search'],
      action: maintenanceService.reindex,
    },
    {
      id: 'delete-events',
      title: 'Apagar Tabela de Dados',
      description: 'Apaga somente a tabela EventsOperations. O indice tambem sera deletado.',
      severity: 'warning',
      icon: <Delete24Regular />,
      affectedItems: ['Tabela EventsOperations', 'Indice Azure Search'],
      action: maintenanceService.deleteEventsData,
    },
    {
      id: 'recalculate-operations-metrics',
      title: 'Recalcular Metricas de Operacoes',
      description: 'Apaga o historico de metricas e forca recalculo completo desde o inicio. Use apos alteracoes na logica de contagem.',
      severity: 'warning',
      icon: <Calculator24Regular />,
      affectedItems: ['OperationsMetricsHistory', 'OperationsMetricsDaily'],
      action: maintenanceService.recalculateOperationsMetrics,
    },
    {
      id: 'delete-all',
      title: 'Apagar Tudo',
      description: 'Apaga TODAS as tabelas e indices incluindo configuracoes de SMTP, alertas e historico.',
      severity: 'destructive',
      icon: <Warning24Regular />,
      affectedItems: [
        'EventsOperations', 'Snapshots', 'DailyMetrics', 'QueueMetricsHistory',
        'OperationsMetricsHistory', 'DlqOperations', 'QueueConfigurations',
        'SmtpConfiguration', 'AlertThresholds', 'QueueThresholds',
        'AlertRecipients', 'AlertHistory', 'DailySummaryHistory', 'DailyQueueMetrics',
        'Indice Azure Search',
      ],
      action: maintenanceService.deleteAll,
    },
  ];

  const handleActionClick = (action: ActionConfig) => {
    setSelectedAction(action);
    setResult(null);
    setJobStatus(null);
    setModalOpen(true);
  };

  const handleConfirm = async (text: string) => {
    if (!selectedAction) return;

    // Para acao de recalculo, usar API assincrona com polling
    if (selectedAction.id === 'recalculate-operations-metrics') {
      try {
        // Iniciar job assincrono
        const job = await maintenanceService.startRecalculateOperationsMetrics(text);
        setJobStatus(job);

        // Polling ate conclusao
        const finalJob = await maintenanceService.pollJobUntilComplete(
          job.id,
          (status) => setJobStatus(status)
        );

        // Converter para MaintenanceResult para compatibilidade
        setResult({
          success: true,
          message: finalJob.message || 'Operacao concluida com sucesso',
          tablesDeleted: finalJob.tablesDeleted,
          indexesDeleted: [],
          duration: `${finalJob.durationSeconds.toFixed(2)}s`,
          errors: []
        });
      } catch (error) {
        setResult({
          success: false,
          message: error instanceof Error ? error.message : 'Erro desconhecido',
          tablesDeleted: [],
          indexesDeleted: [],
          duration: '0s',
          errors: [error instanceof Error ? error.message : 'Erro']
        });
      }
    } else {
      // Outras acoes usam fluxo sincrono
      const res = await selectedAction.action(text);
      setResult(res);
    }

    await loadStatus();
  };

  const getIconStyle = (s: Severity) => s === 'safe' ? styles.iconSafe : s === 'warning' ? styles.iconWarning : styles.iconDestructive;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Settings24Regular />
        <Title2>Manutencao</Title2>
      </div>
      <Text className={styles.subtitle}>
        Operacoes de manutencao do sistema. Todas as acoes requerem confirmacao dupla.
      </Text>

      {result && (
        <MessageBar intent={result.success ? 'success' : 'error'} className={styles.resultMessage}>
          <MessageBarBody>
            {result.message}
            {result.documentsIndexed !== undefined && ` - ${result.documentsIndexed} documentos indexados`}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.cardsContainer}>
        {actions.map((action) => (
          <Card key={action.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={getIconStyle(action.severity)}>{action.icon}</span>
              <Text weight="semibold" size={400}>{action.title}</Text>
            </div>
            <Text className={styles.cardDescription}>{action.description}</Text>
            <Button appearance="secondary" onClick={() => handleActionClick(action)}>
              Executar
            </Button>
          </Card>
        ))}
      </div>

      <div className={styles.statusSection}>
        <Subtitle1 style={{ marginBottom: '16px' }}>Status do Sistema</Subtitle1>
        {loading ? (
          <Spinner label="Carregando..." />
        ) : status && (
          <div className={styles.statusCards}>
            <Card className={styles.statusCard}>
              <Subtitle1>Tabelas</Subtitle1>
              {status.tables.map((t) => (
                <div key={t.name} className={styles.statusItem}>
                  <Text>{t.name}</Text>
                  {t.exists ? <CheckmarkCircle24Regular className={styles.statusExists} /> : <DismissCircle24Regular className={styles.statusNotExists} />}
                </div>
              ))}
            </Card>
            <Card className={styles.statusCard}>
              <Subtitle1>Indices</Subtitle1>
              {status.indexes.map((idx) => (
                <div key={idx.name} className={styles.statusItem}>
                  <Text>{idx.name}</Text>
                  <Text weight="semibold">{idx.exists ? `${idx.documentCount.toLocaleString()} docs` : 'Nao existe'}</Text>
                </div>
              ))}
            </Card>
          </div>
        )}
      </div>

      {selectedAction && (
        <ConfirmationModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setJobStatus(null);
          }}
          onConfirm={handleConfirm}
          title={selectedAction.title}
          description={selectedAction.description}
          severity={selectedAction.severity}
          affectedItems={selectedAction.affectedItems}
          jobStatus={selectedAction.id === 'recalculate-operations-metrics' ? jobStatus : null}
        />
      )}
    </div>
  );
};

export default MaintenancePage;
